import { Controller } from "@hotwired/stimulus"
import { acquireLoadingOverlay, SLOW_VISIT_OVERLAY_DELAY_MS } from "../lib/slow_visit_overlay"
import { buildGlAlert } from "../lib/gl_alert"

// The one Stimulus→React island bridge (audit F37). It waits for the Vite
// entry to publish
// window.__GOAT_REACT__, loads the island the identifier names, mounts it and
// unmounts it on disconnect.
//
// Registered in index.js under each island's EXISTING identifier so that view
// markup — and the identifier-scoped value attributes it carries
// (`data-react-datatable-bridge-server-data-value`, etc.) — stays byte-for-byte
// unchanged. The identifier resolves the registry key here; adding a new
// island is one map entry plus one register() line, never a new file.
//
// The registry holds LOADERS, not mount functions (#14568): each key is
// `() => Promise<mountFn>` backed by a dynamic import, so a page fetches only
// the islands it hosts. That makes the mount asynchronous, and three things
// follow:
//   1. A connection GENERATION token, not a boolean, decides whether a
//      resolved import may mount. Turbo moves a node (disconnect + connect on
//      the same controller instance) while an import is in flight; a
//      resolution belonging to an earlier generation mounts nothing and
//      touches no state. `element.isConnected` cannot tell those apart.
//   2. A failed import (offline, a chunk hash that no longer exists after a
//      deploy) renders the standard alert with a Retry control. Retry is a
//      REAL attempt: the in-flight memo drops a rejected import, and the
//      browser refetches a module whose fetch failed. After a Retry fails as
//      well, a user-initiated "Reload page" control is offered — never an
//      automatic reload, which would drop a live call or unsaved work.
//   3. Some islands are OPENED by clicks on server-rendered triggers whose
//      delegated listeners live inside the island (Quick Schedule + Calendar
//      Settings). A click that lands before the chunk arrives — or between
//      `createRoot().render()` returning and the island's effect attaching
//      the listener — would be lost, so the bridge keeps the LATEST such
//      intent and hands it to the island through a small handshake
//      (INTENT_CONTRACTS): the island announces `<name>:listening` once its
//      listener is live, and the bridge answers with one `<name>:open` window
//      event carrying the trigger. No click is replayed and nothing polls.
//
// The bundle-readiness poll: MAX_POLL_ATTEMPTS × POLL_INTERVAL_MS = the 10s
// give-up window named in the error below.
const POLL_INTERVAL_MS = 100
const MAX_POLL_ATTEMPTS = 100

const MOUNT_FUNCTIONS = {
  "react-add-custom-field-bridge": "mountAddCustomFieldModal",
  "react-ai-assistant-bridge": "mountAiAssistant",
  "react-crm-billing-bridge": "mountCrmBilling",
  "react-crm-setup-bridge": "mountCrmSetup",
  "react-crm-setup-launcher-bridge": "mountCrmSetupLauncher",
  "react-workflows-bridge": "mountWorkflows",
  "react-conversations-bridge": "mountConversations",
  "react-datatable-bridge": "mountDataTable",
  "react-email-composer-bridge": "mountEmailComposerIsland",
  "react-expense-dashboard-bridge": "mountExpenseDashboard",
  "react-expense-review-grid-bridge": "mountExpenseReviewGrid",
  "react-goat-connect-softphone-bridge": "mountGoatConnectSoftphone",
  "react-guides-bridge": "mountGuides",
  "react-imo-agency-picker-bridge": "mountImoAgencyPicker",
  "react-incoming-call-routing-bridge": "mountIncomingCallRouting",
  "react-lead-score-calibration-bridge": "mountLeadScoreCalibration",
  "react-lead-vendor-bridge": "mountLeadVendorModal",
  "react-phone-my-numbers-bridge": "mountPhoneMyNumbers",
  "react-quick-schedule-bridge": "mountQuickScheduleModal",
  "react-settings-notes-bridge": "mountSettingsNotesIsland",
  "react-tag-list-picker-bridge": "mountTagListPicker",
  "react-verified-caller-id-bridge": "mountVerifiedCallerId",
  "react-wrap-up-buttons-bridge": "mountWrapUpButtonsSettings",
}

// Opener handshake per island (see 3. above). Two opener paths exist and both
// are queued:
//   triggers — attributes the island's own delegated click listener matches
//              with `closest()`; a queued one is delivered as the `open`
//              window event with `detail: { trigger: { attribute, value } }`,
//              the same attribute + value the island reads off a live trigger;
//   events   — window events other page code dispatches to open the island
//              (the appointments calendar's FullCalendar `select` fires
//              `quick-schedule:open` with `{ start, allDay }`); a queued one is
//              re-dispatched verbatim, same name, same detail.
// `ready` is the document event the island dispatches once its listeners are
// attached. Mirrored verbatim in
// app/frontend/react/components/forms/QuickScheduleModal.tsx and
// CalendarSettingsModal.tsx (the two bundles share no module).
export const INTENT_CONTRACTS = {
  "react-quick-schedule-bridge": {
    triggers: {
      "data-quick-schedule-open": { ready: "quick-schedule:listening", open: "quick-schedule:open" },
      "data-quick-schedule-edit": { ready: "quick-schedule:listening", open: "quick-schedule:open" },
      "data-calendar-settings-open": { ready: "calendar-settings:listening", open: "calendar-settings:open" },
    },
    events: {
      "quick-schedule:open": { ready: "quick-schedule:listening" },
    },
  },
  // Import Leads → Add Vendor (#16949); mirrored in mountLeadVendorModal.tsx.
  "react-lead-vendor-bridge": {
    triggers: {
      "data-lead-vendor-new": { ready: "lead-vendor:listening", open: "lead-vendor:open" },
    },
    events: {},
  },
}

// Hosts whose island renders OUT of the page flow (a fixed widget, a modal
// portal): their loading status is announced but kept out of the visual flow,
// where a "Loading…" line would be a stray paragraph at the page bottom.
export const OFF_FLOW_ISLANDS = [
  "react-add-custom-field-bridge",
  "react-crm-setup-launcher-bridge",
  "react-goat-connect-softphone-bridge",
  "react-guides-bridge",
  "react-lead-vendor-bridge",
  "react-quick-schedule-bridge",
]

// Hosts whose island IS the screen (a data table, a full-page app). The
// standard loading state for a screen is the spinning goat, so while the chunk
// loads they borrow the layout's #loading-overlay (lib/slow_visit_overlay) —
// after the same no-flash delay a Turbo visit uses — instead of printing a
// "Loading…" line in the page corner. The status stays, visually hidden, for
// screen readers. The goat is released once the island renders its first
// content, so the screen's own React goat (GoatSpinnerOverlay) takes over in
// the same frame. Hosts embedded in a page (composer, picker, notes card) keep
// the in-flow line: a viewport goat would cover the page around them.
export const SCREEN_ISLANDS = [
  "react-ai-assistant-bridge",
  "react-conversations-bridge",
  "react-crm-billing-bridge",
  "react-crm-setup-bridge",
  "react-datatable-bridge",
  "react-expense-dashboard-bridge",
  "react-expense-review-grid-bridge",
  "react-lead-score-calibration-bridge",
  "react-workflows-bridge",
]

// Longest a mounted screen island may hold the borrowed goat waiting for its
// first content before the bridge lets go of it anyway.
export const GOAT_HANDOFF_MAX_MS = 10000

export const LOADING_COPY = "Loading…"
export const CHUNK_ERROR_COPY = "This part of the page couldn't load. Check your connection and try again."
export const CHUNK_RELOAD_COPY = "Still not loading? Reloading the page fetches the latest version."

// One import per LOADER function: hosts of the same island on one page share
// the in-flight promise, and a later host reuses the settled one. Keyed on the
// loader (a WeakMap) rather than the island name so a re-published registry —
// or a test's fresh one — starts clean. A rejected import is dropped so the
// next attempt calls the loader again instead of re-throwing a cached failure.
const islandImports = new WeakMap()

// The failed chunk's URL as the browser reports it ("Failed to fetch
// dynamically imported module: <url>" in Chrome, "error loading dynamically
// imported module: <url>" in Firefox); null when the message carries none.
// It is TEXT, not a trusted address: allowedChunkUrl decides whether the
// bridge may import it.
function chunkUrlFrom(error) {
  const match = /(https?:\/\/[^\s'"()]+\.m?js)\b/.exec(String(error?.message || error || ""))
  return match ? match[1] : null
}

// The directory the bundler put the entry in: `vite_javascript_tag 'react.tsx'`
// renders one `<script type="module" src=".../assets/react-<hash>.js">`. A
// chunk may be re-imported only from that same-origin directory, as a plain
// `.js` path with no query or fragment — the bundler's own output location,
// never an address taken from an error string alone.
function entryAssetDirectory() {
  const scripts = document.querySelectorAll('script[type="module"][src]')
  for (const script of scripts) {
    let url
    try {
      url = new URL(script.getAttribute("src"), window.location.href)
    } catch {
      continue
    }
    if (url.origin === window.location.origin && /\/react-[^/]+\.js$/.test(url.pathname)) {
      return url.href.slice(0, url.href.lastIndexOf("/") + 1)
    }
  }
  return null
}

function allowedChunkUrl(candidate) {
  if (!candidate) return null
  const base = entryAssetDirectory()
  if (!base) return null
  let url
  try {
    url = new URL(candidate, window.location.href)
  } catch {
    return null
  }
  if (url.origin !== window.location.origin || url.search || url.hash) return null
  if (!url.href.startsWith(base) || !/\.m?js$/.test(url.pathname)) return null
  if (url.pathname.slice(base.length - url.origin.length).includes("/")) return null // no subpaths
  return url.href
}

// The island's mount function out of a freshly imported chunk namespace, by
// its ORIGINAL export name only. A chunk of its own exports it directly; a
// chunk the bundler merged with other modules exposes the module as a nested
// namespace object (the compiled entry reaches `mountDataTable` the same way,
// through that inner namespace), so one level of nesting is searched for the
// exact original name. Minified names are never guessed; anything else is a
// failure, so an unrelated export can never be mounted.
function resolveIslandMount(module, name) {
  if (!module || typeof module !== "object") return null
  if (typeof module[name] === "function") return module[name]
  for (const value of Object.values(module)) {
    if (value && typeof value === "object" && typeof value[name] === "function") return value[name]
  }
  return null
}

function loadIsland(loader) {
  let promise = islandImports.get(loader)
  if (!promise) {
    // The loader is invoked synchronously so the chunk request leaves with the
    // connect (or the ready event) itself, not a microtask later.
    let started
    try {
      started = Promise.resolve(loader())
    } catch (error) {
      started = Promise.reject(error)
    }
    promise = started
      .then((mount) => {
        if (typeof mount !== "function") throw new TypeError("island loader did not resolve to a mount function")
        return mount
      })
    islandImports.set(loader, promise)
    promise.catch(() => {
      if (islandImports.get(loader) === promise) islandImports.delete(loader)
    })
  }
  return promise
}

export default class extends Controller {
  static values = {
    config: String, // datatable only — the DataTableConfig registry key, passed before serverData
    serverData: String,
  }

  connect() {
    if (!MOUNT_FUNCTIONS[this.identifier]) {
      console.error(`[GOAT React] No mount function mapped for identifier "${this.identifier}"`)
      return
    }

    this._generation = (this._generation || 0) + 1
    this._connected = true
    this._mounted = false
    this._failures = 0
    this._failedChunkUrl = null
    this._beginIntentCapture()

    if (this._loader()) {
      this._load()
      return
    }

    // Vite scripts haven't executed yet — load on the ready event.
    this._onReady = () => {
      this._clearPoll()
      this._load()
    }
    document.addEventListener("goat-react:ready", this._onReady, { once: true })

    // Fallback: poll in case goat-react:ready fired before this listener
    // attached (the missed-event race every bridge copy guarded against).
    this._pollAttempts = 0
    this._pollInterval = setInterval(() => {
      this._pollAttempts += 1
      if (this._loader()) {
        this._clearPoll()
        this._removeReadyListener()
        this._load()
      } else if (this._pollAttempts >= MAX_POLL_ATTEMPTS) {
        this._clearPoll()
        console.error(`[GOAT React] React bundle did not load within 10s — ${this.identifier} never mounted`)
      }
    }, POLL_INTERVAL_MS)
  }

  disconnect() {
    this._connected = false
    this._mounted = false
    // Remove the ready listener even if it never fired — otherwise a Turbo nav
    // away before goat-react:ready would leave a stale listener that later
    // mounts React onto a detached element with no _unmount ever registered.
    this._removeReadyListener()
    this._clearPoll()
    this._endIntentCapture()
    this._removeStatus()
    this._stopGoatHandoffTimer()
    this._endGoat()
    this._removeError()
    this._stopFirstContentObserver()
    if (this._unmount) {
      const unmount = this._unmount
      this._unmount = null
      unmount()
    }
  }

  // --- readiness -----------------------------------------------------------

  _removeReadyListener() {
    if (this._onReady) {
      document.removeEventListener("goat-react:ready", this._onReady)
      this._onReady = null
    }
  }

  _clearPoll() {
    if (this._pollInterval) {
      clearInterval(this._pollInterval)
    }
    this._pollInterval = null
  }

  _loader() {
    const name = MOUNT_FUNCTIONS[this.identifier]
    const loader = name ? window.__GOAT_REACT__?.[name] : undefined
    return typeof loader === "function" ? loader : undefined
  }

  // --- loading and mounting --------------------------------------------------

  // Only the generation that started an import may act on its outcome, and
  // only while still connected and not yet mounted.
  _current(generation) {
    return this._connected && this._generation === generation && !this._mounted
  }

  _load() {
    const generation = this._generation
    this._removeError()
    this._showStatus()
    loadIsland(this._loader()).then(
      (mount) => {
        if (!this._current(generation)) return
        this._mount(mount)
      },
      (error) => {
        if (!this._current(generation)) return
        this._chunkFailed(error)
      }
    )
  }

  _chunkFailed(error) {
    this._failures += 1
    this._failedChunkUrl = allowedChunkUrl(chunkUrlFrom(error)) || this._failedChunkUrl || null
    console.error(`[GOAT React] ${this.identifier}: island failed to load:`, error)
    this._removeStatus()
    this._endGoat()
    this._renderError()
  }

  // Retry. The HTML module map remembers a FAILED module URL for the life of
  // the page, so re-running the loader (the same `import()` specifier) can
  // never refetch — it rejects at once from the cached failure. When the
  // browser named the failed chunk (Chrome and Firefox do) AND that address is
  // the bundler's own asset directory (allowedChunkUrl), the chunk is imported
  // again under a cache-busting query: a new URL, a real request, the same
  // file, with its relative imports resolving to the shared chunks already in
  // the module map — so no runtime (React, the softphone) is ever evaluated
  // twice. Only the chunk the browser named is ever re-imported. When a
  // DEPENDENCY chunk was the failure, what happens next is the browser's call:
  // through Chrome 155 the re-imported island resolves that dependency to its
  // cached failure and fails again, and the honest recovery is the
  // user-initiated Reload offered after the second failure; from Chrome 156
  // the module map no longer keeps a failed fetch, so the dependency is
  // requested again (at its own URL) and the island mounts. Both outcomes are
  // truthful and the bridge needs no branch for either. Without a usable
  // URL (Safari's message carries none) the loader is retried as-is.
  _retry() {
    if (!this._connected || this._mounted) return
    const url = this._failedChunkUrl
    if (!url) {
      this._load()
      return
    }
    const generation = this._generation
    const loader = this._loader()
    const name = MOUNT_FUNCTIONS[this.identifier]
    this._removeError()
    this._showStatus()
    // allowedChunkUrl only admits a URL with no query string, so `?` is always right.
    const retryUrl = `${url}?goat-retry=${Date.now()}`
    let started
    try {
      started = Promise.resolve(this._importUrl(retryUrl))
    } catch (error) {
      started = Promise.reject(error)
    }
    started
      .then((module) => {
        const mount = resolveIslandMount(module, name)
        if (!mount) throw new TypeError(`retried chunk has no ${name} export`)
        // Sibling hosts of this island reuse the recovered module.
        if (loader) islandImports.set(loader, Promise.resolve(mount))
        return mount
      })
      .then(
        (mount) => {
          if (!this._current(generation)) return
          this._mount(mount)
        },
        (error) => {
          if (!this._current(generation)) return
          this._chunkFailed(error)
        }
      )
  }

  // The one dynamic-URL import in the bridge (Vite must not analyse it;
  // tests stub it — jsdom has no module loader).
  _importUrl(url) {
    return import(/* @vite-ignore */ url)
  }

  _mount(mount) {
    if (this._mounted) return
    this._mounted = true
    this._removeStatus()

    let serverData = {}
    try {
      serverData = JSON.parse(this.serverDataValue || "{}")
    } catch (e) {
      console.error(`[GOAT React] ${this.identifier}: failed to parse serverData:`, e)
    }

    const args = [this.element]
    if (this.hasConfigValue && this.configValue) args.push(this.configValue)
    args.push(serverData)

    try {
      this._unmount = mount(...args)
      this._endGoatOnContent()
    } catch (e) {
      this._endGoat()
      console.error(`[GOAT React] ${this.identifier}: mount failed:`, e)
    }

    this._observeContent()
  }

  // --- loading status --------------------------------------------------------

  _hostEmpty() {
    return !this.element.firstElementChild && this.element.textContent.trim() === ""
  }

  _showStatus() {
    if (this._status || !this._hostEmpty()) return
    const screen = SCREEN_ISLANDS.includes(this.identifier)
    const status = document.createElement("p")
    status.className = screen || OFF_FLOW_ISLANDS.includes(this.identifier)
      ? "visually-hidden"
      : "text-muted small mb-0"
    status.setAttribute("role", "status")
    status.setAttribute("data-react-bridge-loading", "")
    status.textContent = LOADING_COPY
    this.element.appendChild(status)
    this._status = status
    if (screen) this._scheduleGoat()
  }

  _removeStatus() {
    if (this._status) {
      this._status.remove()
      this._status = null
    }
  }

  // --- screen goat -------------------------------------------------------------

  _scheduleGoat() {
    if (this._goatTimer || this._releaseGoat) return
    this._goatTimer = setTimeout(() => {
      this._goatTimer = null
      this._releaseGoat = acquireLoadingOverlay()
    }, SLOW_VISIT_OVERLAY_DELAY_MS)
  }

  _endGoat() {
    if (this._goatTimer) clearTimeout(this._goatTimer)
    this._goatTimer = null
    if (this._releaseGoat) {
      const release = this._releaseGoat
      this._releaseGoat = null
      release()
    }
  }

  // Hand-off: keep the borrowed goat until the island has committed content,
  // so no frame shows an empty screen between the bridge goat and React's. A
  // chunk that arrived inside the no-flash delay never shows the bridge goat.
  _endGoatOnContent() {
    if (!this._releaseGoat || this.element.firstElementChild || typeof MutationObserver === "undefined") {
      this._endGoat()
      return
    }
    // Idempotent: whichever of first content and the timeout comes second is a no-op.
    const finish = () => {
      this._stopGoatHandoffTimer()
      this._endGoat()
    }
    this._whenFirstContent(finish)
    // An island that mounts but never renders (a render error with no
    // boundary) must not leave the whole page covered.
    this._goatHandoffTimer = setTimeout(finish, GOAT_HANDOFF_MAX_MS)
  }

  _stopGoatHandoffTimer() {
    if (this._goatHandoffTimer) clearTimeout(this._goatHandoffTimer)
    this._goatHandoffTimer = null
  }

  // --- first content -------------------------------------------------------------

  // ONE childList observer for every consumer of "the island committed its
  // first element" (the goat hand-off and the performance mark), so the
  // condition is written once. Runs +callback+ now when content is already
  // there; callers guard on MutationObserver themselves.
  _whenFirstContent(callback) {
    if (this.element.firstElementChild) {
      callback()
      return
    }
    this._firstContentCallbacks = [...(this._firstContentCallbacks || []), callback]
    if (this._firstContentObserver) return
    this._firstContentObserver = new MutationObserver(() => {
      if (!this.element.firstElementChild) return
      const callbacks = this._firstContentCallbacks
      this._stopFirstContentObserver()
      callbacks.forEach((run) => run())
    })
    this._firstContentObserver.observe(this.element, { childList: true })
  }

  _stopFirstContentObserver() {
    if (this._firstContentObserver) this._firstContentObserver.disconnect()
    this._firstContentObserver = null
    this._firstContentCallbacks = []
  }

  // --- chunk failure -----------------------------------------------------------

  _renderError() {
    this._removeError()
    // The assertive region holds the copy only (FORM-2 / A11Y-3): the controls
    // sit beside it, never inside it.
    const text = document.createElement("p")
    text.className = "mb-2"
    text.setAttribute("role", "alert")
    text.textContent = this._failures > 1 ? `${CHUNK_ERROR_COPY} ${CHUNK_RELOAD_COPY}` : CHUNK_ERROR_COPY

    const actions = document.createElement("div")
    actions.className = "d-flex flex-wrap gap-2"
    actions.appendChild(this._errorButton("Retry", "data-react-bridge-retry", () => this._retry()))
    if (this._failures > 1) {
      actions.appendChild(this._errorButton("Reload page", "data-react-bridge-reload", () => this._reloadPage()))
    }

    // role: null — the live region is the copy above, not the whole box.
    const box = buildGlAlert({
      tone: "danger",
      icon: "ph-warning-circle",
      className: "gl-alert--compact",
      role: null,
      attributes: { "data-react-bridge-error": "" },
      body: [text, actions],
    })
    this.element.prepend(box)
    this._error = box
  }

  _errorButton(label, marker, onClick) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "gl-btn gl-btn-outline gl-btn-sm"
    button.setAttribute(marker, "")
    button.textContent = label
    button.addEventListener("click", onClick)
    return button
  }

  // The only place the bridge ever navigates, and only from the user's click
  // on the Reload control (jsdom cannot redefine window.location, so tests
  // observe this seam).
  _reloadPage() {
    window.location.reload()
  }

  _removeError() {
    if (this._error) {
      this._error.remove()
      this._error = null
    }
  }

  // --- performance mark ----------------------------------------------------------

  // `goat-react:island:<identifier>:content` is set when the host first holds
  // rendered content — after React's asynchronous first commit, not when
  // `createRoot().render()` returns. Measurement reads it; nothing else does.
  _observeContent() {
    if (typeof MutationObserver === "undefined" || typeof performance?.mark !== "function") return
    this._whenFirstContent(() => performance.mark(`goat-react:island:${this.identifier}:content`))
  }

  // --- opener intent handshake -----------------------------------------------------

  // The bridge keeps ONE intent: the user's latest opener, whichever path it
  // came through. A live intent the island handled itself (a consumed click,
  // or an event dispatched while its island is listening) also replaces the
  // queue — with nothing — so an older queued opener can never reopen on top
  // of what the user did later.
  _beginIntentCapture() {
    const contract = INTENT_CONTRACTS[this.identifier]
    if (!contract) return
    this._intent = null
    this._listening = new Set()
    this._delivering = false
    // WINDOW, bubble phase: it runs AFTER the island's document listener, so a
    // click the island already consumed (defaultPrevented) is visible as such.
    this._onWindowClick = (event) => this._captureTriggerClick(event, contract)
    window.addEventListener("click", this._onWindowClick)
    this._onOpenEvent = new Map()
    Object.entries(contract.events).forEach(([name, { ready }]) => {
      const handler = (event) => this._captureOpenEvent(name, ready, event)
      this._onOpenEvent.set(name, handler)
      window.addEventListener(name, handler)
    })
    this._onIslandListening = new Map()
    this._readyEvents(contract).forEach((ready) => {
      const handler = () => this._islandListening(ready, contract)
      this._onIslandListening.set(ready, handler)
      document.addEventListener(ready, handler)
    })
  }

  _endIntentCapture() {
    this._intent = null
    this._stopUserIntentCapture()
    if (this._onIslandListening) {
      this._onIslandListening.forEach((handler, ready) => document.removeEventListener(ready, handler))
      this._onIslandListening = null
    }
    this._listening = null
  }

  _stopUserIntentCapture() {
    if (this._onWindowClick) {
      window.removeEventListener("click", this._onWindowClick)
      this._onWindowClick = null
    }
    if (this._onOpenEvent) {
      this._onOpenEvent.forEach((handler, name) => window.removeEventListener(name, handler))
      this._onOpenEvent = null
    }
  }

  _readyEvents(contract) {
    return new Set([...Object.values(contract.triggers), ...Object.values(contract.events)].map((c) => c.ready))
  }

  _captureTriggerClick(event, contract) {
    const target = event.target
    if (!(target instanceof Element)) return
    for (const [attribute, { ready, open }] of Object.entries(contract.triggers)) {
      const trigger = target.closest(`[${attribute}]`)
      if (!trigger) continue
      if (event.defaultPrevented) {
        this._intent = null // the island's own listener took it: latest intent is live
        return
      }
      event.preventDefault()
      this._intent = { kind: "trigger", attribute, value: trigger.getAttribute(attribute), ready, open }
      this._deliverIntent()
      return
    }
  }

  _captureOpenEvent(name, ready, event) {
    if (this._delivering) return // our own re-dispatch of a queued event
    if (this._listening.has(ready)) {
      this._intent = null // the island is live and handles it: latest intent is live
      return
    }
    this._intent = { kind: "event", name, detail: event.detail, ready }
  }

  _islandListening(ready, contract) {
    this._listening.add(ready)
    this._deliverIntent()
    const allReady = [...this._readyEvents(contract)].every((name) => this._listening.has(name))
    if (allReady) this._stopUserIntentCapture()
  }

  _deliverIntent() {
    const intent = this._intent
    if (!intent || !this._listening.has(intent.ready)) return
    this._intent = null
    this._delivering = true
    try {
      if (intent.kind === "trigger") {
        window.dispatchEvent(new CustomEvent(intent.open, {
          detail: { trigger: { attribute: intent.attribute, value: intent.value } },
        }))
      } else {
        window.dispatchEvent(new CustomEvent(intent.name, { detail: intent.detail }))
      }
    } finally {
      this._delivering = false
    }
  }
}
